import React, { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Alert, AlertDescription } from "../ui/alert";
import { Spinner } from "../ui/spinner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  Shield,
  Edit,
  Trash2,
  User,
  Building,
  Users,
  Calendar,
  Clock,
  Star,
  AlertTriangle,
  Plus,
  X,
  MapPin,
  Zap,
  Search,
  ExternalLink,
} from "lucide-react";
import { ControlService } from "../../services/controlService";
import { RiskService } from "../../services/riskService";
import ApplyControlToRiskDialog from "./ApplyControlToRiskDialog";
import { useNavigate } from "react-router-dom";
import EditControlForm from "./EditControlForm";

const ControlDetails = ({
  control,
  onClose,
  onControlUpdated,
  onControlDeleted,
  getAccessTokenSilently,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [controlDetails, setControlDetails] = useState(null);
  const [showApplyToRisk, setShowApplyToRisk] = useState(false);
  const [availableRisks, setAvailableRisks] = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [categoryManagementLoading, setCategoryManagementLoading] =
    useState(false);
  const [isEditFormOpen, setIsEditFormOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadControlDetails();
  }, [control.id]);

  const loadControlDetails = async () => {
    try {
      setLoading(true);
      const details = await ControlService.getControlDetails(
        control.id,
        getAccessTokenSilently
      );

      const normalizedDetails = {
        ...details,
        Risks: details.Risks || details.risks || [],
      };
      setControlDetails(normalizedDetails);

      // Load available risks for applying control
      if (normalizedDetails.business_unit_id) {
        // For business unit controls, get risks from the same business unit
        const risks = await RiskService.getRisksForBusinessUnit(
          normalizedDetails.business_unit_id,
          getAccessTokenSilently
        );
        setAvailableRisks(
          risks.filter(
            (risk) =>
              !normalizedDetails.Risks?.some(
                (appliedRisk) => appliedRisk.id === risk.id
              )
          )
        );
      } else {
        // For organization controls, get all organization risks
        const risks = await RiskService.getOrganizationRisks(
          normalizedDetails.organization_id,
          getAccessTokenSilently
        );
        setAvailableRisks(
          risks.filter(
            (risk) =>
              !normalizedDetails.Risks?.some(
                (appliedRisk) => appliedRisk.id === risk.id
              )
          )
        );
      }
    } catch (err) {
      setError(err.message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this control?")) {
      return;
    }

    try {
      setLoading(true);
      await ControlService.deleteControl(control.id, getAccessTokenSilently);
      onControlDeleted(control.id);
      onClose();
    } catch (err) {
      setError(err.message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleControlUpdated = (updatedControl) => {
    setControlDetails(updatedControl);
    onControlUpdated(updatedControl);
  };

  const handleRemoveFromRisk = async (riskId) => {
    if (
      !window.confirm(
        "Are you sure you want to remove this control from the risk?"
      )
    ) {
      return;
    }

    try {
      await ControlService.removeControlFromRisk(
        control.id,
        riskId,
        getAccessTokenSilently
      );
      // Reload control details to refresh the risks list
      loadControlDetails();
    } catch (err) {
      setError(err.message);
      console.error(err);
    }
  };

  const handleControlAppliedToRisk = () => {
    loadControlDetails();
    setShowApplyToRisk(false);
  };

  const loadAvailableCategories = async () => {
    try {
      const categories = await ControlService.getAvailableCategories(
        control.id,
        getAccessTokenSilently
      );
      setAvailableCategories(categories);
    } catch (err) {
      console.error("Failed to load available categories:", err);
    }
  };

  const handleAddCategory = async (categoryId) => {
    try {
      setCategoryManagementLoading(true);
      await ControlService.addCategoryToControl(
        control.id,
        categoryId,
        getAccessTokenSilently
      );
      // Reload control details to get updated categories
      loadControlDetails();
      loadAvailableCategories();
    } catch (err) {
      setError(err.message);
      console.error(err);
    } finally {
      setCategoryManagementLoading(false);
    }
  };

  const handleRemoveCategory = async (categoryId) => {
    if (
      !window.confirm(
        "Are you sure you want to remove this category? This will remove ALL sources (manual and automatic) of this category from the control."
      )
    ) {
      return;
    }

    try {
      setCategoryManagementLoading(true);
      await ControlService.removeCategoryFromControl(
        control.id,
        categoryId,
        getAccessTokenSilently
      );
      // Reload control details to get updated categories
      loadControlDetails();
      loadAvailableCategories();
    } catch (err) {
      setError(err.message);
      console.error(err);
    } finally {
      setCategoryManagementLoading(false);
    }
  };

  const getFrequencyColor = (frequency) => {
    const colors = {
      "ad-hoc": "bg-gray-100 text-gray-800",
      daily: "bg-green-100 text-green-800",
      weekly: "bg-blue-100 text-blue-800",
      monthly: "bg-purple-100 text-purple-800",
      quarterly: "bg-orange-100 text-orange-800",
      annually: "bg-red-100 text-red-800",
    };
    return colors[frequency] || "bg-gray-100 text-gray-800";
  };

  const getLevelColor = (hasBusinessUnit) => {
    return hasBusinessUnit
      ? "bg-green-100 text-green-800"
      : "bg-blue-100 text-blue-800";
  };

  if (loading) {
    return (
      <Dialog open onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <div className="flex justify-center items-center py-8">
            <Spinner size="lg" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!controlDetails) {
    return (
      <Dialog open onOpenChange={onClose}>
        <DialogContent className="max-w-4xl">
          <Alert className="border-red-200 bg-red-50">
            <AlertDescription className="text-red-800">
              Failed to load control details
            </AlertDescription>
          </Alert>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog open onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <div className="space-y-1">
                <h2 className="text-xl font-semibold">Control Details</h2>
                <p className="text-sm text-muted-foreground">
                  {controlDetails.name}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsEditFormOpen(true)}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDelete}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>

          {error && (
            <Alert className="border-red-200 bg-red-50 mb-4">
              <AlertDescription className="text-red-800">
                {error}
              </AlertDescription>
            </Alert>
          )}

          <Tabs defaultValue="overview" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="risks">Associated Risks</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="mt-6">
              <Card>
                <CardContent className="pt-6">
                  <div className="space-y-6">
                    {/* Control Type and Frequency */}
                    <div className="flex flex-wrap gap-4">
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">
                          Frequency
                        </label>
                        <div className="mt-1">
                          <Badge
                            variant="outline"
                            className={`text-xs ${getFrequencyColor(
                              controlDetails.frequency
                            )}`}
                          >
                            <Clock className="h-3 w-3 mr-1" />
                            {controlDetails.frequency}
                          </Badge>
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">
                          Control Type
                        </label>
                        <div className="mt-1">
                          <Badge
                            variant="outline"
                            className={`text-xs ${
                              controlDetails.is_preventive
                                ? "bg-green-100 text-green-800"
                                : "bg-orange-100 text-orange-800"
                            }`}
                          >
                            <Shield className="h-3 w-3 mr-1" />
                            {controlDetails.is_preventive
                              ? "Preventive"
                              : "Detective"}
                          </Badge>
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">
                          Automation
                        </label>
                        <div className="mt-1">
                          <Badge
                            variant="outline"
                            className={`text-xs ${
                              controlDetails.is_automated
                                ? "bg-blue-100 text-blue-800"
                                : "bg-gray-100 text-gray-800"
                            }`}
                          >
                            <Zap className="h-3 w-3 mr-1" />
                            {controlDetails.is_automated
                              ? "Automated"
                              : "Manual"}
                          </Badge>
                        </div>
                      </div>
                      {controlDetails.is_key_control && (
                        <div>
                          <label className="text-sm font-medium text-muted-foreground">
                            Status
                          </label>
                          <div className="mt-1">
                            <Badge
                              variant="outline"
                              className="text-xs bg-yellow-100 text-yellow-800"
                            >
                              <Star className="h-3 w-3 mr-1" />
                              Key Control
                            </Badge>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Description */}
                    {controlDetails.description && (
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">
                          Description
                        </label>
                        <p className="mt-1 text-sm">
                          {controlDetails.description}
                        </p>
                      </div>
                    )}

                    {/* Categories */}
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">
                        Categories
                      </label>
                      <div className="mt-2 space-y-2">
                        {controlDetails.categories &&
                        controlDetails.categories.length > 0 ? (
                          controlDetails.categories.map((category) => (
                            <div
                              key={category.id}
                              className="flex items-start gap-2 p-3 border rounded-lg"
                            >
                              <Shield className="h-4 w-4 text-blue-600 mt-0.5" />
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <p className="text-sm font-medium">
                                    {category.name}
                                  </p>
                                  <Badge variant="outline" className="text-xs">
                                    {category.id_prefix}
                                  </Badge>
                                  {category.sources?.some(
                                    (s) => s.type === "auto_risk_association"
                                  ) && (
                                    <Badge
                                      variant="outline"
                                      className="text-xs bg-orange-50 text-orange-800 border-orange-300"
                                    >
                                      Auto
                                    </Badge>
                                  )}
                                  {category.sources?.some(
                                    (s) => s.type === "manual"
                                  ) && (
                                    <Badge
                                      variant="outline"
                                      className="text-xs bg-blue-50 text-blue-800 border-blue-300"
                                    >
                                      Manual
                                    </Badge>
                                  )}
                                </div>
                                {category.description && (
                                  <p className="text-xs text-muted-foreground">
                                    {category.description}
                                  </p>
                                )}
                                {/* Source details */}
                                {category.sources &&
                                  category.sources.length > 0 && (
                                    <div className="mt-1 text-xs text-muted-foreground">
                                      {category.sources.map((source, idx) => (
                                        <div key={idx}>
                                          {source.type === "manual" &&
                                            source.created_by && (
                                              <span>
                                                Added by{" "}
                                                {source.created_by.name}
                                              </span>
                                            )}
                                          {source.type ===
                                            "auto_risk_association" &&
                                            source.source_risk && (
                                              <span>
                                                Auto-assigned from risk:{" "}
                                                {source.source_risk.name ||
                                                  source.source_risk
                                                    .description}
                                              </span>
                                            )}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="text-sm text-muted-foreground">
                            No categories assigned
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Assignment and Creation Info */}
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">
                          Created By
                        </label>
                        <div className="mt-1 flex items-center gap-2">
                          <User className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <p className="text-sm font-medium">
                              {controlDetails.Creator?.name || "Unknown"}
                            </p>
                            {controlDetails.Creator?.email && (
                              <p className="text-xs text-muted-foreground">
                                {controlDetails.Creator.email}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>

                      {controlDetails.Assignee && (
                        <div>
                          <label className="text-sm font-medium text-muted-foreground">
                            Assigned To
                          </label>
                          <div className="mt-1 flex items-center gap-2">
                            <User className="h-4 w-4 text-muted-foreground" />
                            <div>
                              <p className="text-sm font-medium">
                                {controlDetails.Assignee.name}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {controlDetails.Assignee.email}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {!controlDetails.Assignee &&
                        controlDetails.business_unit_id && (
                          <div>
                            <label className="text-sm font-medium text-muted-foreground">
                              Assigned To
                            </label>
                            <div className="mt-1 flex items-center gap-2">
                              <User className="h-4 w-4 text-muted-foreground" />
                              <div>
                                <p className="text-sm text-muted-foreground">
                                  No one assigned
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                      <div>
                        <label className="text-sm font-medium text-muted-foreground">
                          Created Date
                        </label>
                        <div className="mt-1 flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">
                            {new Date(
                              controlDetails.created_at
                            ).toLocaleDateString()}
                          </span>
                        </div>
                      </div>

                      <div>
                        <label className="text-sm font-medium text-muted-foreground">
                          Level
                        </label>
                        <div className="mt-1 flex items-center gap-2">
                          <Building className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">
                            {controlDetails.business_unit_id
                              ? "Business Unit"
                              : "Organization"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="risks" className="mt-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">Associated Risks</h3>
                  <Button
                    onClick={() => setShowApplyToRisk(true)}
                    size="sm"
                    className="flex items-center gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    Apply to Risk
                  </Button>
                </div>

                {controlDetails.Risks && controlDetails.Risks.length > 0 ? (
                  <Card>
                    <CardContent className="p-0">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Risk Name</TableHead>
                            <TableHead>Category</TableHead>
                            <TableHead>Business Unit</TableHead>
                            <TableHead className="text-right">
                              Actions
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {controlDetails.Risks.map((risk) => (
                            <TableRow key={risk.id}>
                              <TableCell>
                                <div>
                                  <p className="font-medium">
                                    {risk.name || risk.description}
                                  </p>
                                  {risk.description &&
                                    risk.name !== risk.description && (
                                      <p className="text-sm text-muted-foreground">
                                        {risk.description}
                                      </p>
                                    )}
                                  {risk.Category && (
                                    <p className="text-sm text-muted-foreground">
                                      {risk.Category.name}
                                    </p>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>
                                {risk.Category ? (
                                  <span className="text-gray-900">
                                    {risk.Category.name}
                                  </span>
                                ) : (
                                  <span className="text-gray-500">
                                    Uncategorized
                                  </span>
                                )}
                              </TableCell>
                              <TableCell>
                                {risk.BusinessUnit ? (
                                  <div className="flex items-center gap-2">
                                    <Building className="h-4 w-4 text-gray-500" />
                                    <span>{risk.BusinessUnit.name}</span>
                                  </div>
                                ) : (
                                  <span className="text-gray-500">
                                    Organization-wide
                                  </span>
                                )}
                              </TableCell>
                              <TableCell className="text-right">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleRemoveFromRisk(risk.id)}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                ) : (
                  <Card>
                    <CardContent className="flex flex-col items-center justify-center p-8">
                      <AlertTriangle className="h-12 w-12 text-muted-foreground mb-4" />
                      <h3 className="text-lg font-semibold mb-2">
                        No Associated Risks
                      </h3>
                      <p className="text-muted-foreground text-center mb-4">
                        This control is not currently applied to any risks.
                      </p>
                      <Button
                        onClick={() => setShowApplyToRisk(true)}
                        className="flex items-center gap-2"
                      >
                        <Plus className="h-4 w-4" />
                        Apply to Risk
                      </Button>
                    </CardContent>
                  </Card>
                )}

                <div className="flex justify-end mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      onClose();
                      if (controlDetails.business_unit_id) {
                        navigate(
                          `/business-units/${controlDetails.business_unit_id}/risks`
                        );
                      } else {
                        navigate(
                          `/organizations/${controlDetails.organization_id}/risks`
                        );
                      }
                    }}
                    className="flex items-center gap-2"
                  >
                    <ExternalLink className="h-4 w-4" />
                    View All{" "}
                    {controlDetails.business_unit_id
                      ? "Business Unit"
                      : "Organization"}{" "}
                    Risks
                  </Button>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {showApplyToRisk && (
        <ApplyControlToRiskDialog
          control={controlDetails}
          availableRisks={availableRisks}
          onClose={() => setShowApplyToRisk(false)}
          onControlApplied={handleControlAppliedToRisk}
          getAccessTokenSilently={getAccessTokenSilently}
        />
      )}

      {isEditFormOpen && (
        <EditControlForm
          control={controlDetails}
          onClose={() => setIsEditFormOpen(false)}
          onControlUpdated={(updatedControl) => {
            handleControlUpdated(updatedControl);
            setIsEditFormOpen(false);
          }}
          getAccessTokenSilently={getAccessTokenSilently}
        />
      )}
    </>
  );
};

export default ControlDetails;
